﻿import { billetStatus } from "@/shared/selectors/BilletStatusSelector";
import { getMomentTz, MesfModal } from "@dexteel/mesf-core";
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Typography,
} from "@mui/material";
import { useState } from "react";
import { Event } from "../../models/Event";

const moment = getMomentTz();

const formatNumber = (value: number | null | undefined) => {
	if (value === null || value === undefined) return "—";
	return value.toFixed(2);
};

const formatPercentage = (value: number | null | undefined) => {
	if (value === null || value === undefined) return "—";
	return `${value.toFixed(2)}%`;
};

interface TableHeaderProps {
	columns: string[];
	gridTemplateColumns?: string;
	sticky?: boolean;
}

const TableHeader = ({
	columns,
	gridTemplateColumns,
	sticky = false,
}: TableHeaderProps) => (
	<Box
		sx={{
			display: "grid",
			gridTemplateColumns:
				gridTemplateColumns || `repeat(${columns.length}, 1fr)`,
			gap: 3,
			py: 0.65,
			px: 2,
			borderBottom: "2px solid",
			borderColor: "divider",
			backgroundColor: "background.default",
			...(sticky && {
				position: "sticky",
				top: 0,
				zIndex: 1,
			}),
		}}
	>
		{columns.map((col) => (
			<Typography
				key={col}
				variant="caption"
				color="textSecondary"
				fontWeight={600}
			>
				{col}
			</Typography>
		))}
	</Box>
);

interface TableRowProps {
	values: React.ReactNode[];
	gridTemplateColumns?: string;
}

const TableRow = ({ values, gridTemplateColumns }: TableRowProps) => (
	<Box
		sx={{
			display: "grid",
			gridTemplateColumns:
				gridTemplateColumns || `repeat(${values.length}, 1fr)`,
			gap: 2,
			alignItems: "center",
			py: 1,
			px: 2,
			border: "1px solid",
			borderColor: "divider",
			borderTop: "none",
			backgroundColor: "background.default",
		}}
	>
		{values.map((value, idx) => (
			<Typography key={idx} variant="body2">
				{value}
			</Typography>
		))}
	</Box>
);

interface PlannedScheduledActualSectionProps {
	title: string;
	columns: string[];
	values: React.ReactNode[];
}

const PlannedScheduledActualSection = ({
	title,
	columns,
	values,
}: PlannedScheduledActualSectionProps) => (
	<Box sx={{ mt: 3 }}>
		<Typography
			variant="subtitle2"
			color="textSecondary"
			fontWeight="bold"
			sx={{ px: 2, mb: 1 }}
		>
			{title}
		</Typography>
		<TableHeader columns={columns} />
		<TableRow values={values} />
	</Box>
);

interface StyledBadgeProps {
	children: React.ReactNode;
	backgroundColor?: string;
}

const StyledBadge = ({ children, backgroundColor }: StyledBadgeProps) => (
	<Box
		sx={{
			backgroundColor: backgroundColor || "action.hover",
			borderRadius: "16px",
			px: backgroundColor ? 1 : 2,
			py: 0.5,
			textAlign: "center",
		}}
	>
		{children}
	</Box>
);

const detailedColumns = [
	"Charged Tons",
	"Rolled Tons",
	"Finished Tons",
	"TPH",
	"Rolling Hours",
	"Mill Yield",
	"Finished Yield",
];

interface ConfirmDeleteEventModalProps {
	externalLoading: boolean;
	open: boolean;
	onHide: () => void;
	onDelete: (
		eventId: number | null,
		deleteMode: "Main" | "Draft" | "Both",
	) => void;
	event: Event | null;
	calledFrom: "Main" | "Draft";
}

export const ConfirmDeleteEventModal = ({
	externalLoading,
	open,
	onHide,
	onDelete,
	event,
	calledFrom,
}: ConfirmDeleteEventModalProps) => {
	const [deleteAlsoOnDraft, setDeleteAlsoOnDraft] = useState(true);

	const eventGridColumns = "100px 250px 150px 150px";

	const handleClose = () => {
		setDeleteAlsoOnDraft(true);
		onHide();
	};

	const handleDelete = () => {
		onDelete(event?.EventId!, deleteAlsoOnDraft ? "Both" : calledFrom);
		setDeleteAlsoOnDraft(true);
	};

	const getDuration = () => {
		const endTime = event?.End?.getTime() ?? new Date().getTime();
		const startTime = event?.Start?.getTime() ?? new Date().getTime();
		return moment
			.duration(endTime - startTime, "milliseconds")
			.format("w [weeks], d [days], h [hours], m [minutes]", { trim: "both" });
	};

	const sectionBgColor = billetStatus.find(
		(s) => s.code === event?.BilletsStatusCode,
	)?.backgroundColor;

	const deadlineChip = event?.Deadline ? (
		<StyledBadge>
			<Typography variant="body2" fontWeight="bold" color="text.secondary">
				{moment(event.Deadline).format("DD/MM/YY HH:mm")}
			</Typography>
		</StyledBadge>
	) : (
		"—"
	);

	const sections: PlannedScheduledActualSectionProps[] = [
		{
			title: "Scheduled",
			columns: ["Deadline", ...detailedColumns],
			values: [
				deadlineChip,
				formatNumber(event?.ScheduledChargedTons),
				formatNumber(event?.ScheduledRolledTons),
				formatNumber(event?.ScheduledFinishedTons),
				formatNumber(event?.BudgetTPH),
				formatNumber(event?.ScheduledRollingHours),
				formatPercentage(event?.ScheduledMillYield),
				formatPercentage(event?.ScheduledFinishedYield),
			],
		},
		{
			title: "Actual",
			columns: detailedColumns,
			values: [
				formatNumber(event?.ActualChargedTons),
				formatNumber(event?.ActualRolledTons),
				formatNumber(event?.ActualFinishedTons),
				formatNumber(event?.ActualTPH),
				formatNumber(event?.ActualRollingHours),
				formatPercentage(event?.ActualMillYield),
				formatPercentage(event?.ActualFinishedYield),
			],
		},
	];

	return (
		<>
			<MesfModal
				open={open}
				handleClose={handleClose}
				title="Confirm Delete of Event"
				maxWidth={false}
				fullWidth
			>
				<MesfModal.Content dividers>
					<Box sx={{ maxHeight: "70vh", overflowY: "auto" }}>
						{/* Event Info Table */}
						<TableHeader
							columns={["Section", "Duration", "From", "To"]}
							gridTemplateColumns={eventGridColumns}
							sticky
						/>

						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: eventGridColumns,
								gap: 3,
								alignItems: "center",
								py: 0.65,
								px: 2,
								border: "1px solid",
								borderColor: "divider",
								borderTop: "none",
								cursor: "pointer",
								backgroundColor: "background.default",
								"&:hover": {
									backgroundColor: "action.hover",
								},
							}}
						>
							<StyledBadge backgroundColor={sectionBgColor}>
								<Typography variant="body2" fontWeight={500}>
									{event?.SectionName || "No Section"}
								</Typography>
							</StyledBadge>

							<Typography variant="body2">{getDuration()}</Typography>

							<StyledBadge>
								<Typography
									variant="body2"
									fontWeight="bold"
									color="text.secondary"
								>
									{moment(event?.Start).format("DD/MM/YY HH:mm")}
								</Typography>
							</StyledBadge>

							<StyledBadge>
								<Typography
									variant="body2"
									fontWeight="bold"
									color="text.secondary"
								>
									{moment(event?.End).format("DD/MM/YY HH:mm")}
								</Typography>
							</StyledBadge>
						</Box>

						{/* Metrics Sections */}
						{sections.map((section, index) => (
							<Box
								key={section.title}
								sx={index === sections.length - 1 ? { mb: 2 } : {}}
							>
								<PlannedScheduledActualSection
									title={section.title}
									columns={section.columns}
									values={section.values}
								/>
							</Box>
						))}
					</Box>

					{calledFrom === "Main" && (
						<FormControlLabel
							control={
								<Checkbox
									checked={deleteAlsoOnDraft}
									color="primary"
									onChange={(_, checked) => setDeleteAlsoOnDraft(checked)}
									slotProps={{
										input: {
											"data-test-id": "delete-also-on-draft-checkbox",
										} as React.InputHTMLAttributes<HTMLInputElement>,
									}}
								/>
							}
							label="Delete event also on 'Draft'"
						/>
					)}
				</MesfModal.Content>

				<MesfModal.Actions sx={{ px: 3, py: 2 }}>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							width: "100%",
						}}
					>
						<Button
							variant="outlined"
							onClick={handleClose}
							disabled={externalLoading}
						>
							Cancel
						</Button>
						<Button
							variant="contained"
							color="error"
							onClick={handleDelete}
							disabled={externalLoading}
							data-test-id="confirm-delete-button"
						>
							Delete on {calledFrom} {deleteAlsoOnDraft ? "and Draft" : ""}
						</Button>
					</Box>
				</MesfModal.Actions>
			</MesfModal>
		</>
	);
};
